
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../functions/AuthContext";
import BarraSuperior from "../barra_superior";
import { puedeVerSOS } from "./acceso_sos";
import { normalizarItem, normalizarNodo, esAlertaSonable, formatearFecha, idSOS } from "./datos_sos";
import {
  crearMemoriaVigilancia,
  calcularVigilancia,
  sembrarConocidas,
  olvidarAlerta,
  VENTANA_POR_NIT,
} from "./logica_vigilancia";
import {
  prepararAlertas,
  sonarAlerta,
  detenerSonido,
  notificar,
  iniciarParpadeoTitulo,
  detenerParpadeoTitulo,
  pedirPermisoNotificaciones,
  estadoAlertas,
} from "../../../functions/notificaciones_sos";
import "../../css/modulo_sos_prueba.css";


const TIPOS = ["Coacción", "Fuego", "Médica"];

const CLIENTES_DEMO = [
  { nit: "900111111", cliente: "CONJUNTO LOS ALMENDROS (PRUEBA)" },
  { nit: "900222222", cliente: "EDIFICIO MIRADOR (PRUEBA)" },
];

const NOMBRES_DEMO = [
  "Ana Restrepo", "Carlos Muñoz", "Luisa Cárdenas", "Jorge Betancur", "Marta Ospina",
];

const alAzar = (lista) => lista[Math.floor(Math.random() * lista.length)];


const ModuloSOSPrueba = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  const irASOS = useRef(() => {});
  irASOS.current = () => navigate("/sos");

  const [remoto, setRemoto] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [avisosActivos, setAvisosActivos] = useState(true);
  const [diagnostico, setDiagnostico] = useState(estadoAlertas());

  const memoria = useRef(crearMemoriaVigilancia());
  const contador = useRef(0);

  const registrar = useCallback((texto, tono = "info") => {
    const hora = new Date().toLocaleTimeString("es-CO", { hour12: false });
    setBitacora((previa) => [{ hora, texto, tono, id: Math.random() }, ...previa].slice(0, 60));
  }, []);

  const aplicarVentana = useCallback((alertas) => {
    const porNit = new Map();
    alertas.forEach((a) => {
      if (!porNit.has(a.nit)) porNit.set(a.nit, []);
      porNit.get(a.nit).push(a);
    });

    return [...porNit.values()].flatMap((lista) =>
      [...lista].sort((a, b) => (a.key < b.key ? -1 : 1)).slice(-VENTANA_POR_NIT)
    );
  }, []);

  const evaluar = useCallback((alertas) => {
    const veredicto = calcularVigilancia(memoria.current, aplicarVentana(alertas));
    setResultado(veredicto);

    if (veredicto.fueraDeVentana.length) {
      registrar(
        `${veredicto.fueraDeVentana.length} alerta(s) salieron de la ventana pero se siguen contando: ${veredicto.fueraDeVentana.map((a) => a.key).join(", ")}`,
        "aviso"
      );
    }

    if (!veredicto.aviso) return veredicto;

    const { tipo, alertas: implicadas } = veredicto.aviso;

    if (!avisosActivos) {
      registrar(`Veredicto "${tipo}" (avisos silenciados en esta prueba)`, "aviso");
      return veredicto;
    }

    if (tipo === "nueva") {
      sonarAlerta(3);
      const notificada = notificar({
        titulo: "🚨 [PRUEBA] ¡Alerta SOS!",
        cuerpo: `${implicadas[0].cliente} · ${implicadas[0].tipo}`,
        alHacerClic: () => irASOS.current?.(),
      });
      registrar(
        `Aviso de alerta NUEVA: sonido ×3${notificada ? " + notificación del navegador" : " (notificación no disponible)"}`,
        "alerta"
      );
    } else {
      sonarAlerta(1);
      notificar({
        titulo: `🚨 [PRUEBA] ${implicadas.length} SOS sin atender`,
        cuerpo: "Siguen pendientes de gestión.",
        alHacerClic: () => irASOS.current?.(),
      });
      registrar(`Recordatorio de ${implicadas.length} pendiente(s): sonido ×1`, "alerta");
    }

    setDiagnostico(estadoAlertas());
    return veredicto;
  }, [aplicarVentana, avisosActivos, registrar]);

  const sincronizarTitulo = useCallback((veredicto) => {
    if (!avisosActivos || !veredicto?.pendientes.length) {
      detenerParpadeoTitulo();
      return;
    }
    iniciarParpadeoTitulo(`🚨 [PRUEBA] ${veredicto.pendientes.length} SOS`);
  }, [avisosActivos]);

  const aplicar = useCallback((nuevasAlertas, mensaje) => {
    prepararAlertas();
    setRemoto(nuevasAlertas);
    if (mensaje) registrar(mensaje);
    const veredicto = evaluar(nuevasAlertas);
    sincronizarTitulo(veredicto);
  }, [evaluar, registrar, sincronizarTitulo]);


  const crearAlerta = useCallback((cliente, tipo, minutosAtras = 0) => {
    contador.current += 1;
    const key = `-SIM${String(contador.current).padStart(4, "0")}`;
    const creacion = new Date(Date.now() - minutosAtras * 60000);

    return normalizarItem(key, {
      cliente: cliente.cliente,
      nit: cliente.nit,
      direccion: "Torre 1 Apto 101",
      nombreCompleto: alAzar(NOMBRES_DEMO),
      fechayhoraCreacion: formatearFecha(creacion),
      latitud: "4.7110",
      longitud: "-74.0721",
      correo: "residente.prueba@ejemplo.com",
      telefono: "3001234567",
      estado: "Pendiente",
      tipo,
    }, cliente.nit);
  }, []);

  const simularSOS = (tipo, cliente = CLIENTES_DEMO[0]) => {
    const alerta = crearAlerta(cliente, tipo);
    aplicar([...remoto, alerta], `SOS simulado: ${tipo} en ${cliente.cliente}`);
  };

  const arrancarDesde = (previas, mensaje) => {
    memoria.current = crearMemoriaVigilancia();
    sembrarConocidas(memoria.current, previas);
    aplicar(previas, mensaje);
  };

  const simularCargaConPendientes = () => {
    arrancarDesde(
      [
        crearAlerta(CLIENTES_DEMO[0], "Coacción", 60),
        crearAlerta(CLIENTES_DEMO[1], "Médica", 90),
      ],
      "Carga inicial con 2 pendientes viejos: deben contarse, pero NO sonar como nuevos"
    );
  };

  const simularRecargaConAlertaReciente = () => {
    arrancarDesde(
      [
        crearAlerta(CLIENTES_DEMO[0], "Coacción", 120),
        crearAlerta(CLIENTES_DEMO[1], "Fuego", 0),
      ],
      "Recarga con una alerta de hace un instante: esa SÍ debe sonar"
    );
  };

  const simularDesborde = () => {
    const base = Array.from({ length: VENTANA_POR_NIT }, (_, i) =>
      crearAlerta(CLIENTES_DEMO[0], alAzar(TIPOS), 60 + i)
    );
    arrancarDesde(base, `Carga inicial con ${VENTANA_POR_NIT} alertas (el tamaño de la ventana)`);

    setTimeout(() => {
      const extra = crearAlerta(CLIENTES_DEMO[0], "Fuego");
      aplicar([...base, extra], "Entra una más: la más vieja sale de la ventana");
    }, 900);
  };

  const simularBasura = () => {
    const nodoCrudo = {
      "-VACIO":    {},
      "-NULO":     null,
      "-TEXTO":    "esto no es una alerta",
      "-ARREGLO":  [1, 2, 3],
      "-NUMERO":   42,
      "-A_MEDIAS": { estado: "Pendiente" },
    };

    const filas = normalizarNodo(nodoCrudo, CLIENTES_DEMO[0].nit);
    const sonables = filas.filter(esAlertaSonable);

    registrar(
      `Basura inyectada: de 6 nodos, ${filas.length} pasaron el filtro y ${sonables.length} pueden sonar.`,
      sonables.length ? "alerta" : "aviso"
    );

    aplicar([...remoto, ...filas], "Nodos inválidos procesados por normalizarNodo()");
  };

  const cerrarUna = (alerta) => {
    const actualizadas = remoto.map((a) =>
      idSOS(a) === idSOS(alerta) ? { ...a, estado_final: "Cerrado" } : a
    );
    olvidarAlerta(memoria.current, idSOS(alerta));
    aplicar(actualizadas, `Cerrada ${alerta.key} (${alerta.cliente})`);
  };

  const cerrarTodas = () => {
    remoto.forEach((a) => olvidarAlerta(memoria.current, idSOS(a)));
    aplicar(
      remoto.map((a) => ({ ...a, estado_final: "Cerrado" })),
      "Todas las alertas cerradas"
    );
  };

  const reiniciar = () => {
    memoria.current = crearMemoriaVigilancia();
    contador.current = 0;
    detenerSonido();
    detenerParpadeoTitulo();
    setRemoto([]);
    setResultado(null);
    setBitacora([]);
    registrar("Simulación reiniciada");
  };


  const probarSonido = () => {
    prepararAlertas();
    sonarAlerta(1);
    setTimeout(() => setDiagnostico(estadoAlertas()), 400);
    registrar("Prueba de sonido lanzada. Si no se oye, revisa el volumen y que hayas hecho clic en la página.", "aviso");
  };

  const probarNotificacion = async () => {
    const permiso = await pedirPermisoNotificaciones();
    setDiagnostico(estadoAlertas());
    const ok = notificar({
      titulo: "🚨 [PRUEBA] Notificación de CoordiPH",
      cuerpo: "Tócala: debe llevarte al listado de SOS.",
      alHacerClic: () => irASOS.current?.(),
    });
    registrar(
      ok ? "Notificación enviada." : `No se pudo notificar (permiso: ${permiso}).`,
      ok ? "info" : "aviso"
    );
  };

  const probarTitulo = () => {
    iniciarParpadeoTitulo("🚨 [PRUEBA] SOS");
    registrar("Título parpadeando. Cambia de pestaña para verlo. Se detiene en 10 s.", "aviso");
    setTimeout(detenerParpadeoTitulo, 10000);
  };


  const enVentana = useMemo(() => {
    const ids = new Set(aplicarVentana(remoto).map(idSOS));
    return ids;
  }, [remoto, aplicarVentana]);

  const abiertas = remoto.filter((a) => a.estado_final !== "Cerrado");


  if (!usuario) return null;

  if (!puedeVerSOS(usuario.email)) {
    return (
      <>
        <BarraSuperior />
        <div className="msp">
          <div className="msp-caja">
            <h1>Módulo de prueba de SOS</h1>
            <p>Esta herramienta es solo para las cuentas que atienden alertas SOS.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BarraSuperior />

      <div className="msp">
        <div className="msp-aviso-seguro">
          <i className="bi bi-shield-check" />
          <div>
            <strong>Modo prueba — nada de esto sale de tu navegador.</strong>
            <span>
              No se escribe en Firebase, no se crea ningún registro de SOS y no se
              notifica a ningún celular. El sonido, la notificación del navegador y
              el parpadeo del título sí son los reales, para que veas qué pasa.
            </span>
          </div>
        </div>

        <div className="msp-cabecera">
          <div>
            <h1>Módulo de prueba de SOS</h1>
            <p>
              Simula alertas y observa cómo reacciona exactamente la misma lógica que
              corre en producción. Ventana vigilada: {VENTANA_POR_NIT} alertas por cliente.
            </p>
          </div>

          <label className="msp-interruptor">
            <input
              type="checkbox"
              checked={avisosActivos}
              onChange={(e) => {
                setAvisosActivos(e.target.checked);
                if (!e.target.checked) { detenerSonido(); detenerParpadeoTitulo(); }
              }}
            />
            Avisos activos (sonido y notificación)
          </label>
        </div>

        <section className="msp-seccion">
          <h2>1. Simular una alerta</h2>
          <div className="msp-botones">
            {TIPOS.map((tipo) => (
              <button key={tipo} type="button" className="msp-btn msp-btn-rojo" onClick={() => simularSOS(tipo)}>
                <i className="bi bi-exclamation-triangle-fill" /> {tipo}
              </button>
            ))}
            <button
              type="button"
              className="msp-btn"
              onClick={() => simularSOS(alAzar(TIPOS), CLIENTES_DEMO[1])}
            >
              <i className="bi bi-building" /> Otro cliente
            </button>
          </div>
        </section>

        <section className="msp-seccion">
          <h2>2. Escenarios completos</h2>
          <div className="msp-botones">
            <button type="button" className="msp-btn" onClick={simularCargaConPendientes}>
              Recarga con pendientes viejos
            </button>
            <button type="button" className="msp-btn" onClick={simularRecargaConAlertaReciente}>
              Recarga con alerta reciente
            </button>
            <button type="button" className="msp-btn" onClick={simularDesborde}>
              Desborde de ventana ({VENTANA_POR_NIT}+1)
            </button>
            <button type="button" className="msp-btn" onClick={simularBasura}>
              Inyectar datos inválidos
            </button>
            <button type="button" className="msp-btn" onClick={cerrarTodas} disabled={!abiertas.length}>
              Cerrar todas
            </button>
            <button type="button" className="msp-btn" onClick={reiniciar}>
              Reiniciar
            </button>
          </div>
          <p className="msp-nota">
            <strong>Recarga con pendientes viejos</strong>: al abrir CoordiPH las alertas que ya
            estaban en Firebase se cuentan pero no gritan «¡alerta nueva!». Debe sonar una sola
            vez, como recordatorio.
            <br />
            <strong>Recarga con alerta reciente</strong>: el caso contrario. Si alguien apretó el
            botón hace un instante, esa sí suena como nueva aunque aparezca en la primera carga.
            <br />
            <strong>Desborde</strong>: una alerta que se sale de la ventana vigilada se sigue
            contando en vez de desaparecer.
            <br />
            <strong>Datos inválidos</strong>: mete nodos nulos, vacíos, textos, arreglos y un
            registro a medio escribir por el mismo filtro que procesa Firebase. Ninguno debe
            hacer sonar la alarma.
          </p>
        </section>

        <section className="msp-seccion">
          <h2>3. Probar cada canal por separado</h2>
          <div className="msp-botones">
            <button type="button" className="msp-btn" onClick={probarSonido}>
              <i className="bi bi-volume-up" /> Sonido
            </button>
            <button type="button" className="msp-btn" onClick={probarNotificacion}>
              <i className="bi bi-bell" /> Notificación
            </button>
            <button type="button" className="msp-btn" onClick={probarTitulo}>
              <i className="bi bi-window" /> Parpadeo del título
            </button>
          </div>

          <div className="msp-diagnostico">
            <span className={diagnostico.sonidoDesbloqueado ? "msp-ok" : "msp-pendiente"}>
              Audio: {diagnostico.sonidoDesbloqueado ? "desbloqueado" : "aún no desbloqueado (haz clic en la página)"}
            </span>
            <span className={diagnostico.permiso === "granted" ? "msp-ok" : "msp-pendiente"}>
              Notificaciones: {diagnostico.permiso}
            </span>
          </div>
        </section>

        <div className="msp-columnas">
          <section className="msp-seccion">
            <h2>Veredicto de la lógica</h2>
            {!resultado ? (
              <p className="msp-vacio">Aún no has simulado nada.</p>
            ) : (
              <>
                <div className="msp-metricas">
                  <div><span>{resultado.pendientes.length}</span>Pendientes</div>
                  <div><span>{resultado.nuevas.length}</span>Nuevas en sesión</div>
                  <div><span>{resultado.fueraDeVentana.length}</span>Fuera de ventana</div>
                </div>

                <p className="msp-veredicto">
                  Último aviso:{" "}
                  {resultado.aviso
                    ? <code>{resultado.aviso.tipo}</code>
                    : <em>ninguno</em>}
                </p>

                <table className="msp-tabla">
                  <thead>
                    <tr><th>Clave</th><th>Cliente</th><th>Tipo</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {remoto.length === 0 && (
                      <tr><td colSpan={5} className="msp-vacio">Sin alertas simuladas</td></tr>
                    )}
                    {remoto.map((a) => (
                      <tr key={idSOS(a)} className={a.estado_final === "Cerrado" ? "msp-cerrada" : ""}>
                        <td>
                          <code>{a.key}</code>
                          {!enVentana.has(idSOS(a)) && (
                            <span className="msp-etiqueta" title="Ya no viene en el snapshot, pero se sigue contando">
                              fuera de ventana
                            </span>
                          )}
                          {!esAlertaSonable(a) && (
                            <span
                              className="msp-etiqueta msp-etiqueta-muda"
                              title="Sin fecha de creación interpretable: se cuenta y se muestra, pero nunca hace sonar la alarma"
                            >
                              no suena
                            </span>
                          )}
                        </td>
                        <td>{a.cliente}</td>
                        <td>{a.tipo}</td>
                        <td>{a.estado_final}</td>
                        <td>
                          {a.estado_final !== "Cerrado" && (
                            <button type="button" className="msp-btn-mini" onClick={() => cerrarUna(a)}>
                              Cerrar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          <section className="msp-seccion">
            <h2>Bitácora</h2>
            {!bitacora.length ? (
              <p className="msp-vacio">Las acciones aparecerán aquí.</p>
            ) : (
              <ul className="msp-bitacora">
                {bitacora.map((linea) => (
                  <li key={linea.id} className={`msp-linea-${linea.tono}`}>
                    <span className="msp-hora">{linea.hora}</span>
                    {linea.texto}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default ModuloSOSPrueba;
